import { useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { DailyRevenuePosition, RevenueForecast, RevenueInvestment, RevenueSource, RevenueToBudgetReport } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

export function Revenue() {
  const { persona } = useAuth();
  const isCashManager = persona?.roles.includes('MOF_CASH_MANAGER') ?? false;

  const [showSource, setShowSource] = useState(false);
  const [showSweep, setShowSweep] = useState(false);
  const [showInvestment, setShowInvestment] = useState(false);
  const [reportMonth, setReportMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [actionError, setActionError] = useState<string | null>(null);

  const positionQ = useApiQuery<DailyRevenuePosition>('/api/v1/revenue/position/daily');
  const forecastQ = useApiQuery<RevenueForecast>('/api/v1/revenue/forecast?horizon=30');
  const sourcesQ = useApiQuery<{ items: RevenueSource[] }>('/api/v1/revenue/sources');
  const investmentsQ = useApiQuery<{ items: RevenueInvestment[] }>('/api/v1/revenue/investments');
  const reportQ = useApiQuery<RevenueToBudgetReport>(`/api/v1/revenue/reports/revenue-to-budget?month=${reportMonth}`);

  const matureInvestment = async (id: string) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/revenue/investments/${id}/mature`);
      investmentsQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to mature investment');
    }
  };

  return (
    <div>
      <PageHeader
        title="Revenue & Cash Position"
        subtitle="Every rupee coming in — SRC, OpenClearance duty and SeyPay settlements, swept into the Treasury Single Account"
        actions={
          isCashManager ? (
            <div className="flex gap-2">
              <button className="btn-secondary" onClick={() => setShowSource(true)}>
                + Revenue source
              </button>
              <button className="btn-secondary" onClick={() => setShowInvestment(true)}>
                + Place investment
              </button>
              <button className="btn-primary" onClick={() => setShowSweep(true)}>
                Record sweep
              </button>
            </div>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {positionQ.loading ? (
        <Spinner />
      ) : positionQ.error ? (
        <div className="mb-6">
          <ErrorNote message={positionQ.error} />
        </div>
      ) : positionQ.data ? (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Today's collections" value={scr(positionQ.data.todayTotal)} hint={positionQ.data.date} />
          <StatCard label="Month to date" value={scr(positionQ.data.monthToDate)} tone="good" />
          <StatCard
            label="Awaiting sweep into TSA"
            value={scr(positionQ.data.awaitingSweep.amount)}
            hint={`${positionQ.data.awaitingSweep.receipts} receipt(s)`}
            tone={positionQ.data.awaitingSweep.amount > 0 ? 'warn' : 'default'}
          />
          <StatCard label="Sources reporting today" value={positionQ.data.bySource.length} />
        </div>
      ) : null}

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Today's collections by source</h2>
        <div className="card overflow-x-auto">
          {positionQ.error ? (
            <ErrorNote message={positionQ.error} />
          ) : (positionQ.data?.bySource.length ?? 0) === 0 ? (
            <EmptyState title="No collections recorded today" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Source</th>
                  <th className="table-head text-right">Amount</th>
                  <th className="table-head text-right">Receipts</th>
                </tr>
              </thead>
              <tbody>
                {positionQ.data!.bySource.map((s) => (
                  <tr key={s.source}>
                    <td className="table-cell font-medium">{s.source ?? '—'}</td>
                    <td className="table-cell text-right tabular-nums">{scr(s.amount)}</td>
                    <td className="table-cell text-right">{s.receipts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <div>
          <h2 className="mb-2 text-sm font-bold text-slate-700">30-day cash ladder</h2>
          <div className="card max-h-80 overflow-y-auto overflow-x-auto">
            {forecastQ.loading ? (
              <Spinner />
            ) : forecastQ.error ? (
              <ErrorNote message={forecastQ.error} />
            ) : (
              <table className="w-full">
                <thead className="sticky top-0 bg-slate-50">
                  <tr>
                    <th className="table-head">Date</th>
                    <th className="table-head text-right">Inflow</th>
                    <th className="table-head text-right">Outflow</th>
                    <th className="table-head text-right">Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {(forecastQ.data?.series ?? []).map((s) => (
                    <tr key={s.date}>
                      <td className="table-cell text-xs">{s.date}</td>
                      <td className="table-cell text-right tabular-nums text-seagreen-600">{scr(s.inflow)}</td>
                      <td className="table-cell text-right tabular-nums text-rose-600">{scr(s.outflow)}</td>
                      <td className="table-cell text-right font-semibold tabular-nums">{scr(s.closing)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {forecastQ.data ? (
            <p className="mt-1 text-xs text-slate-400">Trailing 30-day average inflow assumption: {scr(forecastQ.data.dailyInflowAssumption)}/day</p>
          ) : null}
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Revenue-to-budget mapping</h2>
            <input className="field-input w-36 !py-1 text-xs" type="month" value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} />
          </div>
          <div className="card overflow-x-auto">
            {reportQ.loading ? (
              <Spinner />
            ) : reportQ.error ? (
              <ErrorNote message={reportQ.error} />
            ) : (reportQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No receipts posted this month" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Budget line</th>
                    <th className="table-head text-right">Amount</th>
                    <th className="table-head text-right">Receipts</th>
                  </tr>
                </thead>
                <tbody>
                  {reportQ.data!.items.map((i) => (
                    <tr key={i.coaCombinationId}>
                      <td className="table-cell text-xs">{i.coaCombinationId}</td>
                      <td className="table-cell text-right tabular-nums">{scr(i.amount)}</td>
                      <td className="table-cell text-right">{i.receipts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {reportQ.data ? <p className="mt-1 text-xs text-slate-400">Total {scr(reportQ.data.total)}</p> : null}
        </div>
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <div>
          <h2 className="mb-2 text-sm font-bold text-slate-700">Revenue sources</h2>
          <div className="card overflow-x-auto">
            {sourcesQ.loading ? (
              <Spinner />
            ) : sourcesQ.error ? (
              <ErrorNote message={sourcesQ.error} />
            ) : (sourcesQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No revenue sources registered" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Code</th>
                    <th className="table-head">Name</th>
                    <th className="table-head">System</th>
                  </tr>
                </thead>
                <tbody>
                  {sourcesQ.data!.items.map((s) => (
                    <tr key={s.id}>
                      <td className="table-cell font-medium">{s.code}</td>
                      <td className="table-cell">{s.name}</td>
                      <td className="table-cell">
                        <StatusBadge status={s.system} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div>
          <h2 className="mb-2 text-sm font-bold text-slate-700">Idle-balance placements</h2>
          <div className="card overflow-x-auto">
            {investmentsQ.loading ? (
              <Spinner />
            ) : investmentsQ.error ? (
              <ErrorNote message={investmentsQ.error} />
            ) : (investmentsQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No investments placed" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Counterparty</th>
                    <th className="table-head text-right">Principal</th>
                    <th className="table-head">Maturity</th>
                    <th className="table-head">Status</th>
                    {isCashManager ? <th className="table-head" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {investmentsQ.data!.items.map((inv) => (
                    <tr key={inv.id}>
                      <td className="table-cell">{inv.counterparty}</td>
                      <td className="table-cell text-right tabular-nums">
                        {scr(inv.principal)} @ {inv.ratePercent}%
                      </td>
                      <td className="table-cell text-slate-500">{formatDate(inv.maturityDate)}</td>
                      <td className="table-cell">
                        <StatusBadge status={inv.status} />
                      </td>
                      {isCashManager ? (
                        <td className="table-cell text-right">
                          {inv.status === 'PLACED' ? (
                            <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => matureInvestment(inv.id)}>
                              Mark matured
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showSource ? (
        <SourceModal
          onClose={() => setShowSource(false)}
          onDone={() => {
            setShowSource(false);
            sourcesQ.reload();
          }}
        />
      ) : null}
      {showSweep ? (
        <SweepModal
          onClose={() => setShowSweep(false)}
          onDone={() => {
            setShowSweep(false);
            positionQ.reload();
          }}
        />
      ) : null}
      {showInvestment ? (
        <InvestmentModal
          onClose={() => setShowInvestment(false)}
          onDone={() => {
            setShowInvestment(false);
            investmentsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function SourceModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [system, setSystem] = useState<RevenueSource['system']>('SRC');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/revenue/sources', { code, name, system });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register source');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register revenue source" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Code">
          <input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. SRC-VAT" />
        </Field>
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. VAT collections" />
        </Field>
        <Field label="Upstream system">
          <select className="field-input" value={system} onChange={(e) => setSystem(e.target.value as RevenueSource['system'])}>
            <option value="SRC">SRC — Seychelles Revenue Commission</option>
            <option value="OPENCLEARANCE">OpenClearance — customs duty</option>
            <option value="SEYPAY">SeyPay — government e-payments</option>
            <option value="OTHER">Other</option>
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !code || !name}>
            {busy ? 'Registering…' : 'Register source'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SweepModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [fromAccount, setFromAccount] = useState('');
  const [toTsa, setToTsa] = useState('CBS-TSA-SCR-001');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [receiptIds, setReceiptIds] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/revenue/sweeps', {
        fromAccount,
        toTsa,
        date,
        receiptIds: receiptIds
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Sweep failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Record a sweep into the TSA" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">
          There is no receipts-listing endpoint yet, so paste the RevenueReceipt IDs to sweep (from the collecting agency's export) below.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="From account (collecting bank)">
            <input className="field-input" value={fromAccount} onChange={(e) => setFromAccount(e.target.value)} placeholder="e.g. SRC-COLLECTIONS-001" />
          </Field>
          <Field label="To TSA (CBS reference)">
            <input className="field-input" value={toTsa} onChange={(e) => setToTsa(e.target.value)} />
          </Field>
        </div>
        <Field label="Sweep date">
          <input className="field-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Receipt IDs (comma or newline separated)">
          <textarea className="field-input" rows={4} value={receiptIds} onChange={(e) => setReceiptIds(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !fromAccount || !toTsa || !receiptIds.trim()}>
            {busy ? 'Sweeping…' : 'Record sweep'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function InvestmentModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [counterparty, setCounterparty] = useState('');
  const [principal, setPrincipal] = useState('');
  const [ratePercent, setRatePercent] = useState('');
  const [placedDate, setPlacedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [maturityDate, setMaturityDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/revenue/investments', {
        counterparty,
        principal: Number.parseFloat(principal),
        ratePercent: Number.parseFloat(ratePercent),
        placedDate,
        maturityDate,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to place investment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Place idle-balance investment" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Counterparty">
          <input className="field-input" value={counterparty} onChange={(e) => setCounterparty(e.target.value)} placeholder="e.g. Nouvobanq Treasury Bill" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Principal (SCR)">
            <input className="field-input" type="number" min="0" step="0.01" value={principal} onChange={(e) => setPrincipal(e.target.value)} />
          </Field>
          <Field label="Rate %">
            <input className="field-input" type="number" min="0" max="50" step="0.001" value={ratePercent} onChange={(e) => setRatePercent(e.target.value)} />
          </Field>
          <Field label="Placed date">
            <input className="field-input" type="date" value={placedDate} onChange={(e) => setPlacedDate(e.target.value)} />
          </Field>
          <Field label="Maturity date">
            <input className="field-input" type="date" value={maturityDate} onChange={(e) => setMaturityDate(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !counterparty || !principal || !ratePercent || !maturityDate}>
            {busy ? 'Placing…' : 'Place investment'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
